/* External */
import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import isEmptyL from 'lodash-es/isEmpty';
import Rating from '@dtvgo/rating';
import smileLoadingGIF from '../../../assets/images/smile-animation.gif';

/* Styles */
import './LoaderScreen.scss';

/* Other */
import {
  getAssetNameWithQualifier, getVrioImageUrl, IMAGE_RESIZERS,
  buildPlayerAssetMetadataV2,
  IS_BRAZIL,
} from '../../../utils';

const badgeTypes = {
  EPISODE: 'player.loaderScreen.badge.episode',
  MOVIE: 'player.loaderScreen.badge.movie',
  SHOW: 'player.loaderScreen.badge.show',
  SPORTS: 'player.loaderScreen.badge.sports',
  SPORT: 'player.loaderScreen.badge.sports',
};

/**
 * @function getTitle
 * @description Returns the title of the loader
 * @param {object} content The content info
 * @param {Function} formatMessage A function to get translations
 */
function getTitle(asset, formatMessage) {
  const {
    episode, live, title,
  } = asset || {};

  const { showName } = episode?.number ? episode : {};
  const { qualifiers } = live || {};

  return getAssetNameWithQualifier(
    qualifiers,
    showName || title,
    formatMessage,
  );
}

function getEpisodeSubtitle({
  episode,
}, assetTitle) {
  const { seasonNumber, number, showName } = episode;

  if ((number ?? 0) <= 0 && assetTitle !== showName) {
    return showName;
  }

  if (!episode || assetTitle === showName) {
    return '';
  }

  return `T${seasonNumber} E${number} • ${assetTitle}`;
}

/**
 * Component LoaderScreen
 * @description Shows a loader page while content is loaded
 */
function LoaderScreen({
  asset, channelName, intl: { formatMessage }, channelLogo,
}) {
  const loading = isEmptyL(asset);
  const {
    type, images, rating, genres, episode, title: assetTitle,
  } = asset;

  const title = loading
    ? formatMessage({
      id: 'player.loaderScreen.loadingContent',
      defaultMessage: 'Estamos cargando tu contenido',
    })
    : getTitle(asset, formatMessage);

  const description = loading
    ? formatMessage({
      id: 'player.loaderScreen.pleaseWait',
      defaultMessage: 'Espere, por favor',
    })
    : genres && buildPlayerAssetMetadataV2(asset, formatMessage);

  let programmingCover = {
    url: '',
  };

  const programmingImage = !loading && images && images.filter(((img) => img.ratio === '16:9'));

  if (programmingImage?.length > 0) {
    programmingCover = programmingImage.pop();
  }

  const badge = (!loading && type) && <span className="dtv-loaderv2-screen__badge">{formatMessage({ id: badgeTypes[type] })}</span>;

  const hasRating = !loading && rating && (
    <Rating
      rating={rating}
      brazil={IS_BRAZIL}
      className=""
    />
  );

  const hasEpisodeTitle = type && type === 'EPISODE' && episode && getEpisodeSubtitle({ episode }, assetTitle);

  return (
    <div
      className="dtv-loaderv2-screen"
      style={{
        background: `url(${programmingCover.url})`,
      }}
    >
      <div style={{
        margin: 'auto',
      }}
      >
        <div className="dtv-loaderv2-screen__wrapper">
          {channelLogo.length > 0 && (
            <img
              src={getVrioImageUrl(channelLogo, '4:3', IMAGE_RESIZERS.CARD_16x9)}
              alt={channelName}
              className="dtv-loaderv2-screen__channel-logo"
            />
          )}
          {badge}
        </div>
        <h1 className="dtv-loaderv2-screen__content-title">{title}</h1>
        <div className="dtv-loaderv2-screen__content-description">
          {hasEpisodeTitle && (
            <span className="dtv-loaderv2-screen__episode-subtitle-container">
              {hasEpisodeTitle}
            </span>
          )}
          <span className="dtv-loaderv2-screen__rating-theme-container">
            {hasRating}
            {description && description}
          </span>
        </div>
      </div>
      <div className="dtv-loaderv2-screen__smile-loading">
        <div className="dtv-loaderv2-screen__smile-heading">
          <div className="dtv-loaderv2-screen__smile-loading-text">{formatMessage({ id: 'player.loaderScreen.loading' })}</div>
          <div className="dtv-loaderv2-screen__dots-container">
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
            <span className="dot" />
          </div>
        </div>
        <img src={smileLoadingGIF} alt="Smile Loading" className="dtv-loaderv2-screen__smile-gif" />
      </div>
    </div>
  );
}

LoaderScreen.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }).isRequired,
  asset: PropTypes.shape(),
  channelName: PropTypes.string,
  channelLogo: PropTypes.arrayOf({}),
};

LoaderScreen.defaultProps = {
  asset: {},
  channelName: '',
  channelLogo: [],
};

export default injectIntl(LoaderScreen);
